import { CloseOutlined } from "@mui/icons-material";
import { Box, Button, Card, CardContent, Fab } from "@mui/material";
import { displayDateAndTime } from "Backend";
import { CaseFormContext } from "contexts/CaseFormContext";
import { CaseExport, ExportStatus, OverallOutcomeStatus } from "generated/graphql";
import formatSizeBytes from "helpers/formatSizeBytes";
import { useSnackbar } from "notistack";
import { MessageEvent } from "pubnub";
import { usePubNub } from "pubnub-react";
import { useContext, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  fetchPossibleChangesFromAutoScripts,
  setCaseInsightsPending,
  setCaseOverallOutcomeStatus,
} from "redux/slices/case";
import { updateExport } from "redux/slices/case";
import { selectUserInfo } from "redux/slices/session";
import { P } from "theme/typography";

interface CaseMessageBase {
  caseId: number;
  timestamp: string;
}

type CaseMessage =
  | ({ type: "ExportCompleteEvent"; export: CaseExport } & CaseMessageBase)
  | ({ type: "OverallOutcomeStatusUpdated"; overallOutcomeStatus: OverallOutcomeStatus } & CaseMessageBase)
  | ({ type: "AutoScriptRunComplete"; runId: string; isFailure: boolean } & CaseMessageBase);

const CasePubnubEvents = ({
  caseId,
  lensId,
  triggerCaseExportDownload,
  children,
}: {
  caseId: number;
  lensId: number;
  triggerCaseExportDownload: (caseId: number, exportId: number) => void;
  children: React.ReactNode;
}) => {
  const pubnub = usePubNub();
  const channel = `case_${caseId}`;
  const dispatch = useAppDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const userInfo = useAppSelector(selectUserInfo);
  const caseFormCtx = useContext(CaseFormContext);

  const listener = useMemo(
    () => ({
      message: (evt: MessageEvent) => {
        if (evt.channel === channel) {
          let message: CaseMessage;
          try {
            message = JSON.parse(evt.message);
          } catch {
            console.error("Failed to parse message", evt.message);
            return;
          }
          switch (message.type) {
            case "ExportCompleteEvent": {
              const exp: CaseExport = message.export;
              dispatch(updateExport(exp));

              if (userInfo?.id === exp.createdBy?.id && exp.status === ExportStatus.Complete) {
                const snackKey = enqueueSnackbar("Case Export Ready", {
                  variant: "success",
                  persist: true,
                  content: (
                    <Box>
                      <Fab size="small" onClick={() => closeSnackbar(snackKey)} sx={{ mb: 2 }}>
                        <CloseOutlined />
                      </Fab>
                      <Card sx={{ width: 300 }}>
                        <CardContent>
                          <P variant="h6">{exp.exportType} Export Ready</P>
                          <Box sx={{ marginY: 1, display: "flex", flexDirection: "column" }}>
                            {exp.completedAt && (
                              <P variant="body5">Completed: {displayDateAndTime(new Date(exp.completedAt))}</P>
                            )}
                            {exp.sizeBytes !== null && exp.sizeBytes !== undefined && (
                              <P variant="body5">Size: {formatSizeBytes(exp.sizeBytes)}</P>
                            )}
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => {
                                triggerCaseExportDownload(exp.caseId, exp.id);
                                closeSnackbar(snackKey);
                              }}>
                              Download
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  ),
                });
              } else if (exp.status === ExportStatus.Failed) {
                enqueueSnackbar("Failed to generate export. Please try again.", { variant: "error" });
              }
              break;
            }
            case "OverallOutcomeStatusUpdated": {
              const status: OverallOutcomeStatus = message.overallOutcomeStatus;
              dispatch(setCaseOverallOutcomeStatus({ status }));
              break;
            }
            case "AutoScriptRunComplete": {
              const isFailure = message.isFailure;
              if (!isFailure && caseFormCtx.autoScriptRunIdsSet.has(message.runId)) {
                Promise.all([
                  dispatch(fetchPossibleChangesFromAutoScripts({ caseId, lensId })),
                  caseFormCtx.resetAllFieldsServer(),
                ])
                  .then(() => {
                    dispatch(setCaseInsightsPending(false));
                    enqueueSnackbar("Case updated", { variant: "success" });
                  })
                  .catch((e) => {
                    console.error(e);
                    enqueueSnackbar("Unable to refresh insights. Please contact support.", {
                      variant: "error",
                    });
                  });
              } else if (isFailure) {
                dispatch(setCaseInsightsPending(false));
                enqueueSnackbar("Unable to refresh insights", { variant: "error" });
              } else {
                dispatch(setCaseInsightsPending(false));
              }
              break;
            }
            default:
          }
        }
      },
    }),
    [caseId, userInfo, caseFormCtx.autoScriptRunIdsSet.values]
  );

  useEffect(() => {
    pubnub.addListener(listener);
    pubnub.subscribe({ channels: [channel] });

    return () => {
      pubnub.removeListener(listener);
      pubnub.unsubscribe({ channels: [channel] });
    };
  }, [pubnub, channel, listener]);

  return <>{children}</>;
};

export default CasePubnubEvents;
